import type {
  AssetId,
  AudioId,
  CharacterId,
  EffectId,
  ThemeId,
  UiAssetId,
} from '../../shared/types/ids';

export type AssetCategory =
  | 'hero'
  | 'guide'
  | 'boss'
  | 'creature'
  | 'ar'
  | 'portal'
  | 'target'
  | 'collectible'
  | 'environment'
  | 'ui'
  | 'effect'
  | 'audio';

export type AssetType = 'image' | 'svg' | 'sprite' | 'rive' | 'audio';

/**
 * PNG is the default production download format, not the only permitted one (D-014).
 *
 * `wav` joins the audio formats for the Phase 5 Slice 0″ placeholders only: they are synthesised
 * locally by `tools/audio/generate-placeholder-audio.mjs` so that no third-party bytes and no
 * licence question enter the repo before the sourcing slice, and producing `ogg`/`mp3` would have
 * meant adding an encoder to the toolchain to generate art nobody has approved. Real audio arrives
 * in the compressed formats that were already here.
 */
export type AssetFormat = 'png' | 'svg' | 'webp' | 'json' | 'mp3' | 'ogg' | 'wav';

/** Asset review state (asset brief §20). Distinct from generation-job status (D-014). */
export type AssetApprovalStatus =
  'draft' | 'reviewing' | 'approved' | 'needs-revision' | 'deprecated';

export interface AssetAnchor {
  /** 0..1 relative to the asset's own bounds. */
  x: number;
  y: number;
}

export interface AssetDefinition {
  /**
   * Manifest ID keeping the trailing `.v[n]` token, e.g. `ar.player-ring.hero-1.ready.v1`.
   * The game prefix is omitted inside a theme package (D-014).
   */
  id: AssetId;
  source: string;
  type: AssetType;
  category: AssetCategory;
  /** State token such as `idle`, `ready`, `correct`. */
  state?: string;
  /** Numeric version mirroring the `.v[n]` token in `id` (D-014). */
  version: number;
  format: AssetFormat;
  transparent: boolean;
  approvalStatus: AssetApprovalStatus;
  preload: boolean;
  width?: number;
  height?: number;
  anchor?: AssetAnchor;
  fallbackAssetId?: AssetId;
  metadata?: Record<string, unknown>;
}

export interface CharacterDefinition {
  id: CharacterId;
  displayName: string;
  /** State name to asset ID, e.g. `{ idle: 'hero.spin.idle.v1' }`. */
  states: Record<string, AssetId>;
  defaultState: string;
  narrationVoiceProfileId?: string;
}

export interface EffectDefinition {
  id: EffectId;
  assetId: AssetId;
  durationMs: number;
  /** Structurally false: no flashing effects are permitted (architecture §23). */
  flashing: false;
}

/**
 * What a sound is for (PS5-2, D-094(3); `narration` added in Slice A″).
 *
 * Ruled necessary *before* the manifest's first audio entry, because it is the difference between
 * a bed that plays under everything and a cue that does not: a field added afterwards would have
 * had to be optional, and an optional category is a category nothing can rely on.
 *
 * `narration` is a spoken line baked to a file (PS5-7). It is a third kind rather than a loud
 * `sfx`, because the three differ in every way that matters to the mixer: a bed loops and ducks, a
 * cue is short and does not, and a spoken line is the one sound in this product a child cannot
 * play without — it carries the question. Calling it a sound effect would have put the platform's
 * `sfx.*` ID rule (AUD-05: exactly one team cue, and no other effect) in permanent conflict with a
 * script of two hundred lines.
 */
export const AUDIO_CATEGORIES = ['sfx', 'music', 'narration'] as const;

/**
 * The categories, as a value as well as a type.
 *
 * One source, because the two had drifted apart the moment a third category existed: the SDK type
 * allowed `narration`, the registry validator's hand-written allow-list did not, and the result was
 * a module that compiled and then refused to launch (Slice A″). A validator that reads this array
 * cannot fall behind the type again.
 */
export type AudioCategory = (typeof AUDIO_CATEGORIES)[number];

export interface AudioDefinition {
  id: AudioId;
  source: string;
  format: AssetFormat;
  loop: boolean;
  /** 0..1, relative to the platform's baseline level for the category. */
  volume: number;
  category: AudioCategory;
  /**
   * Review state, the same five values a picture asset carries (D-014, brief §20).
   *
   * Sound needs it for the same reason art does, and needs it *before* the first entry: the audio
   * that ships in Slice 0″ is synthesised placeholder material nobody has listened to and approved,
   * and a manifest with no way to say so would have been claiming otherwise by omission.
   */
  approvalStatus: AssetApprovalStatus;
  /**
   * Precedence when something else must be heard (PS5-2).
   *
   * The platform attenuates a sound while a foreground claim with a **strictly higher** priority
   * is outstanding, and narration claims the foreground for as long as it speaks. So a music bed
   * declares a number below narration's and ducks; a short cue declares one above it and does not,
   * which is what "SFX never duck anything" means as a property of the sound rather than as an
   * exception in the code. See `src/platform/config/audio.ts` for the narration figure.
   */
  duckPriority: number;
}

export interface ThemeManifest {
  id: ThemeId;
  version: string;
  assets: Record<AssetId, AssetDefinition>;
  characters: Record<CharacterId, CharacterDefinition>;
  effects: Record<EffectId, EffectDefinition>;
  audio: Record<AudioId, AudioDefinition>;
  ui: Record<UiAssetId, AssetDefinition>;
  /** Generic placeholder per category, used when an ID does not resolve (D-014). */
  categoryPlaceholders: Partial<Record<AssetCategory, AssetId>>;
}

export interface ResolvedAsset {
  assetId: AssetId;
  definition: AssetDefinition;
  /** True when the requested ID was missing and a category placeholder was substituted. */
  usedFallback: boolean;
}

/**
 * Scenes resolve every asset through this. An unresolved ID falls back to the category
 * placeholder — never an error, never a blank (D-014). Filenames never appear in scenes.
 */
export interface ThemeAssetResolver {
  /** The best candidate for an ID: the declared asset, or a placeholder if there is none. */
  resolve(assetId: AssetId): ResolvedAsset;

  /**
   * Every candidate for an ID, best first: the declared asset, then its `fallbackAssetId`,
   * then the category placeholder, then the platform placeholder.
   *
   * A loader walks this when a *declared* asset's file fails to load — a stale manifest entry
   * or a missing file must still end in placeholder art rather than a hole in the scene
   * (D-014). Always contains at least one entry.
   */
  resolveChain(assetId: AssetId): ResolvedAsset[];

  has(assetId: AssetId): boolean;
}
